import { useMemo } from 'react';
import { always } from '~/_shared/utils/function.helpers';
import {
  type TranslationFnc, useTranslation,
} from '~/_shared/utils/hooks';
import { createStateSelector } from '~/_shared/utils/memoize/createSelector';
import { notNullsy } from '~/_shared/utils/typeGuards';
import { canMemberManageClient } from '~/clientTeamManagement/memberRoles.helpers';
import { getMainMenuItemTitle } from '~/mainMenu/mainMenu.helpers';
import { MainMenuItems } from '~/mainMenu/mainMenuItem.enum';
import { SharedMapProximityToolInfoComponent } from '~/map/settings/accordion/sharedPresentationalMaps/sharedMapProximityToolInfo.component';
import { EMPTY_MESSAGE } from '~/map/settings/structure.constants';
import {
  filterEnabledPresentationalMapTools, publicMapSettingsMapToolsMenuOpenSelector, publicMapSettingsSelector,
} from '~/store/mapSettings/publicMapSettings/mapSettingsPublicMapSettings.selectors';
import { isCurrentMapSnapshot } from '~/store/selectors/isCurrentMapSnapshot.selector';
import { userDataSelector } from '~/store/userData/userData.selectors';
import {
  type SettingsAccordionSubItem, SettingsAccordionSubItemType,
} from '../settingsAccordionData.type';
import { type SharedPresentationalMapsStructureProps } from './sharedPresentationalMaps.types';

const DISABLED_TOOLTIP_LEGEND_ONLY = 'This option is disabled while "Display Legend Only" is active';

const isMapToolsMenuOpenDisabled = createStateSelector([publicMapSettingsSelector], settings => {
  return settings.displayLegendOnly || !filterEnabledPresentationalMapTools(settings).length;
});

const disabledForMapSnapshotsSelector = createStateSelector([
  isCurrentMapSnapshot,
  (_state, message: string) => message,
], (isSnapshot, message) => isSnapshot ? {
  message,
  isActiveWhenDisabled: false,
} : EMPTY_MESSAGE);

const isMapKeyDisabled = createStateSelector([publicMapSettingsSelector], settings => {
  return !settings.mapKey;
});

const isMapKeyOpenDisabled = createStateSelector([publicMapSettingsSelector], settings => {
  return !settings.mapKeyOpen;
});

const isLocationFinderDisabled = createStateSelector([publicMapSettingsSelector], settings => {
  return !settings.locationFinder;
});

const legendOnlyDisabledTooltipSelector = createStateSelector([
  publicMapSettingsSelector,
  (_state, t: TranslationFnc) => t,
], (settings, t) => ({
  message:
    settings.displayLegendOnly ? t(DISABLED_TOOLTIP_LEGEND_ONLY) : null,
}));

const grupingToolDisabledTooltipSelector = createStateSelector([
  publicMapSettingsSelector,
  (_state, t: TranslationFnc) => t,
], (settings, t) => {
  if (settings.displayLegendOnly) {
    return { message: t(DISABLED_TOOLTIP_LEGEND_ONLY) };
  }
  if (settings.legendReplacesGrouping) {
    return { message: t('This option is turned off when "Legend Replaces Grouping" is enabled') };
  }

  return EMPTY_MESSAGE;
});

const boundaryToolDisabledTooltipSelector = createStateSelector([
  publicMapSettingsSelector,
  (_state, t: TranslationFnc) => t,
], (settings, t) => {
  if (settings.displayLegendOnly) {
    return { message: t(DISABLED_TOOLTIP_LEGEND_ONLY) };
  }
  return EMPTY_MESSAGE;
});

const mapToolsMenuOpenDisabledTooltipSelector = createStateSelector([
  isMapToolsMenuOpenDisabled,
  (_state, t: TranslationFnc) => t,
], (menuDisabled, t) =>
  menuDisabled ? ({ message: t('This option is disabled while there are no tools set to be visible on a public map') }) : EMPTY_MESSAGE);

const defaultMapToolDisabledTooltipSelector = createStateSelector([
  isMapToolsMenuOpenDisabled,
  publicMapSettingsMapToolsMenuOpenSelector,
  (_state, t: TranslationFnc) => t,
], (menuDisabled, menuOpen, t) => {
  if (menuDisabled || !menuOpen) {
    return ({ message: t('This option is disabled while "Map  Tools Menu Open" is inactive.') });
  }
  return EMPTY_MESSAGE;
});

const mapKeyDisabledTooltipSelector = createStateSelector([
  isMapKeyDisabled,
  (_state, t: TranslationFnc) => t,
], (menuDisabled, t) => menuDisabled ? ({ message: t('Turn on {{item}} to enable this option.', { item: t('Map Key') }) }) : EMPTY_MESSAGE);

const defaultMapKeyOpenDisabledTooltipSelector = createStateSelector([
  isMapKeyOpenDisabled,
  (_state, t: TranslationFnc) => t,
], (menuDisabled, t) => menuDisabled ? ({ message: t('Turn on {{item}} to enable this option.', {
  item: t('{{toolName}} Open', { toolName: t('Map Key') }),
}) }) : EMPTY_MESSAGE);

const locationFinderStartsOpenDisabledTooltipSelector = createStateSelector([
  isLocationFinderDisabled,
  (_state, t: TranslationFnc) => t,
], (menuDisabled, t) => menuDisabled ? ({ message: t('Turn on {{item}} to enable this option.', { item: t('Location Finder') }) }) : EMPTY_MESSAGE);

const presentationalMapsExportDisabledTooltipSelector = createStateSelector([
  userDataSelector,
  (_state, t: TranslationFnc) => t,
], (userData, t) => {
  if (userData.limits.allowPublicExportAuthorization) {
    return EMPTY_MESSAGE;
  }

  let message = t('mapSettingsInfo.ExportingDataFeatures.askAdminToAllow', {
    listOfAdmins: userData.clientMembers
      .filter(member => canMemberManageClient(member.role))
      .map(member => `${member.name}`)
      .join(', '),
  });

  if (canMemberManageClient(userData?.clientRole)) {
    message = t('mapSettingsInfo.ExportingDataFeatures.allowForAccountFirst', {
      functionalityName: t('mapSettingsInfo.ExportingDataFeatures'),
      menuName: t('Account Settings'),
      subMenuName: t('Manage Team Users'),
    });
  }

  return ({ message });
});

export const useSharedPresentationalMapsStructure = (props: SharedPresentationalMapsStructureProps): ReadonlyArray<SettingsAccordionSubItem> => {
  const { toggles, buttons, dropdowns } = props;
  const [t] = useTranslation();

  const allItems: ReadonlyArray<SettingsAccordionSubItem | null> = useMemo(() => ([
    {
      label: t('Display Legend Only'),
      type: SettingsAccordionSubItemType.Toggle,
      infoTooltip: t('mapSettingsInfo.DisplayLegendOnly'),
      disabledTooltipSelector: always(EMPTY_MESSAGE),
      ...toggles.displayLegendOnly,
    },
    {
      label: getMainMenuItemTitle(MainMenuItems.Grouping, t),
      type: SettingsAccordionSubItemType.Toggle,
      infoTooltip: t('mapSettingsInfo.GroupingTool'),
      disabledTooltipSelector: state => grupingToolDisabledTooltipSelector(state, t),
      ...toggles.groupingTool,
    },
    {
      label: t('Legend Replaces Grouping'),
      type: SettingsAccordionSubItemType.Toggle,
      infoTooltip: t('mapSettingsInfo.LegendReplacesGrouping'),
      disabledTooltipSelector: state => legendOnlyDisabledTooltipSelector(state, t),
      ...toggles.legendReplacesGrouping,
    },
    {
      label: getMainMenuItemTitle(MainMenuItems.Filter, t),
      type: SettingsAccordionSubItemType.Toggle,
      infoTooltip: t('mapSettingsInfo.FilterTool'),
      disabledTooltipSelector: state => legendOnlyDisabledTooltipSelector(state, t),
      ...toggles.filterTool,
    },
    {
      label: t('Filter Menus Open'),
      type: SettingsAccordionSubItemType.Toggle,
      infoTooltip: t('mapSettingsInfo.FilterMenusOpen'),
      disabledTooltipSelector: always(EMPTY_MESSAGE),
      ...toggles.filterMenusOpen,
    },
    {
      label: getMainMenuItemTitle(MainMenuItems.Routing, t),
      type: SettingsAccordionSubItemType.Toggle,
      infoTooltip: t('mapSettingsInfo.RoutingDirections'),
      disabledTooltipSelector: state => legendOnlyDisabledTooltipSelector(state, t),
      ...toggles.routingDirections,
    },
    {
      label: getMainMenuItemTitle(MainMenuItems.Boundary, t),
      type: SettingsAccordionSubItemType.Toggle,
      infoTooltip: t('mapSettingsInfo.BoundaryTool'),
      disabledTooltipSelector: state => boundaryToolDisabledTooltipSelector(state, t),
      ...toggles.boundaryTool,
    },
    {
      label: getMainMenuItemTitle(MainMenuItems.Proximity, t),
      type: SettingsAccordionSubItemType.Toggle,
      infoTooltip: t('mapSettingsInfo.RadiusProximity'),
      disabledTooltipSelector: state => legendOnlyDisabledTooltipSelector(state, t),
      ...toggles.radiusProximity,
      additionalContent: toggles.radiusProximity.isOn ? (
        <SharedMapProximityToolInfoComponent />
      ) : null,
    },
    {
      label: getMainMenuItemTitle(MainMenuItems.HeatMap, t),
      type: SettingsAccordionSubItemType.Toggle,
      infoTooltip: t('mapSettingsInfo.HeatMappingTool'),
      disabledTooltipSelector: state => legendOnlyDisabledTooltipSelector(state, t),
      ...toggles.heatMappingTool,
    },
    {
      label: t('Map Tools Menu Open'),
      type: SettingsAccordionSubItemType.Toggle,
      infoTooltip: t('mapSettingsInfo.MapToolsMenuOpen'),
      disabledTooltipSelector: state => mapToolsMenuOpenDisabledTooltipSelector(state, t),
      ...toggles.mapToolsMenuOpen,
    },
    {
      label: t('Default Tool'),
      type: SettingsAccordionSubItemType.DropDown,
      infoTooltip: t('mapSettingsInfo.DefaultOpenMapToolPresentationalMap'),
      disabledTooltipSelector: (state) => defaultMapToolDisabledTooltipSelector(state, t),
      ...dropdowns.defaultOpenMapToolPresentationalMap,
    },
    {
      label: t('Map Key'),
      type: SettingsAccordionSubItemType.Toggle,
      infoTooltip: t('mapSettingsInfo.MapKey'),
      disabledTooltipSelector: always(EMPTY_MESSAGE),
      ...toggles.mapKey,
    },
    {
      label: t('{{toolName}} Open', { toolName: t('Map Key') }),
      type: SettingsAccordionSubItemType.Toggle,
      infoTooltip: t('mapSettingsInfo.MapKeyOpen'),
      disabledTooltipSelector: state => mapKeyDisabledTooltipSelector(state, t),
      ...toggles.mapKeyOpen,
    },
    {
      label: t('Default Open {{item}} Type', { item: t('Map Key') }),
      type: SettingsAccordionSubItemType.DropDown,
      infoTooltip: null,
      disabledTooltipSelector: state => defaultMapKeyOpenDisabledTooltipSelector(state, t),
      ...dropdowns.defaultOpenMapKey,
    },
    {
      label: t('Location Finder'),
      type: SettingsAccordionSubItemType.Toggle,
      infoTooltip: t('mapSettingsInfo.LocationFinder'),
      disabledTooltipSelector: always(EMPTY_MESSAGE),
      ...toggles.locationFinder,
    },
    {
      label: t('Location Finder Starts Open'),
      type: SettingsAccordionSubItemType.Toggle,
      infoTooltip: t('mapSettingsInfo.LocationFinderStartsOpen'),
      disabledTooltipSelector: state => locationFinderStartsOpenDisabledTooltipSelector(state, t),
      ...toggles.locationFinderStartsOpen,
    },
    {
      label: t('Display Search Button'),
      type: SettingsAccordionSubItemType.Toggle,
      infoTooltip: t('mapSettingsInfo.DisplaySearchBox'),
      disabledTooltipSelector: always(EMPTY_MESSAGE),
      ...toggles.displaySearchBox,
    },
    {
      label: t('Search Bar Always Visible'),
      type: SettingsAccordionSubItemType.Toggle,
      infoTooltip: t('mapSettingsInfo.SearchBarAlwaysVisible'),
      disabledTooltipSelector: always(EMPTY_MESSAGE),
      ...toggles.searchBarAlwaysVisible,
    },
    {
      label: t('Show Search Bar Filter'),
      type: SettingsAccordionSubItemType.Toggle,
      infoTooltip: t('mapSettingsInfo.ShowSearchFilter', { searchName: t('Search By') }),
      disabledTooltipSelector: always(EMPTY_MESSAGE),
      ...toggles.showSearchFilter,
    },
    {
      label: t('Search Bar Filter Default Option'),
      type: SettingsAccordionSubItemType.DropDown,
      infoTooltip: t('mapSettingsInfo.SearchBarFilterDefaultOption'),
      disabledTooltipSelector: always(EMPTY_MESSAGE),
      ...dropdowns.searchBarFilterDefaultOption,
    },
    {
      label: t('Allow Satellite View'),
      type: SettingsAccordionSubItemType.Toggle,
      infoTooltip: t('mapSettingsInfo.AllowSatelliteView'),
      disabledTooltipSelector: always(EMPTY_MESSAGE),
      ...toggles.allowSatelliteView,
    },
    {
      label: t('Show My Location'),
      type: SettingsAccordionSubItemType.Toggle,
      infoTooltip: t('mapSettingsInfo.ShowMyLocation'),
      disabledTooltipSelector: always(EMPTY_MESSAGE),
      ...toggles.showMyLocation,
    },
    {
      label: t('Show My Location Default'),
      type: SettingsAccordionSubItemType.Toggle,
      infoTooltip: t('mapSettingsInfo.ShowMyLocationDefault'),
      disabledTooltipSelector: always(EMPTY_MESSAGE),
      ...toggles.showMyLocationDefault,
    },
    /*
      We have decided to disable Map Image Export for presentational maps for beta release
      TODO: remove this some time after release of we decide to never use this
      {
        label: t('Map Image Export'),
        type: SettingsAccordionSubItemType.Toggle,
        infoTooltip: t('mapSettingsInfo.MapImageExport'),
        disabledTooltipSelector: always(EMPTY_MESSAGE),
        ...toggles.mapImageExport,
      }
    */
    {
      label: t('Distance Calculator'),
      type: SettingsAccordionSubItemType.Toggle,
      infoTooltip: t('mapSettingsInfo.DistanceCalculator'),
      disabledTooltipSelector: always(EMPTY_MESSAGE),
      ...toggles.distanceCalculator,
    },
    {
      label: t('Lasso Tool'),
      type: SettingsAccordionSubItemType.Toggle,
      infoTooltip: t('mapSettingsInfo.LassoTool'),
      disabledTooltipSelector: always(EMPTY_MESSAGE),
      ...toggles.lassoTool,
    },
    {
      label: t('Display Title & Description'),
      type: SettingsAccordionSubItemType.Toggle,
      infoTooltip: t('mapSettingsInfo.DisplayTitleAndDescription'),
      disabledTooltipSelector: always(EMPTY_MESSAGE),
      ...toggles.displayTitleAndDescription,
    },
    {
      label: t('Theme Style'),
      type: SettingsAccordionSubItemType.Toggle,
      infoTooltip: null,
      disabledTooltipSelector: always(EMPTY_MESSAGE),
      ...toggles.themeStyle,
    },
    {
      label: t('Allow Street View'),
      type: SettingsAccordionSubItemType.Toggle,
      infoTooltip: t('mapSettingsInfo.AllowStreetView'),
      disabledTooltipSelector: always(EMPTY_MESSAGE),
      ...toggles.allowStreetView,
    },
    {
      label: t('Markers Visibility Button'),
      type: SettingsAccordionSubItemType.Toggle,
      infoTooltip: t('mapSettingsInfo.MarkersVisibilityButton'),
      disabledTooltipSelector: always(EMPTY_MESSAGE),
      ...toggles.markersVisibilityButton,
    },
    {
      label: t('Map Zoom'),
      type: SettingsAccordionSubItemType.Toggle,
      infoTooltip: t('mapSettingsInfo.MapZoom'),
      disabledTooltipSelector: always(EMPTY_MESSAGE),
      ...toggles.mapZoom,
    },
    {
      label: t('Restrict Zoom Levels'),
      textSelector: () => t('Open'),
      type: SettingsAccordionSubItemType.Button,
      infoTooltip: t('mapSettingsInfo.RestrictZoomLevels'),
      disabledTooltipSelector: always(EMPTY_MESSAGE),
      ...buttons.restrictZoomLevels,
    },
    {
      label: t('Restrict Map Panning'),
      type: SettingsAccordionSubItemType.Toggle,
      infoTooltip: t('mapSettingsInfo.RestrictMapPanning'),
      disabledTooltipSelector: always(EMPTY_MESSAGE),
      ...toggles.restrictMapPanning,
    },
    {
      type: SettingsAccordionSubItemType.Button,
      label: t('Enable/Disable Columns'),
      disabledTooltipSelector: state => disabledForMapSnapshotsSelector(state, t('mapSettingsInfo.showHideDataDisabled')),
      infoTooltip: t('mapSettingsInfo.enableDisableColumns'),
      textSelector: () => t('Open'),
      ...buttons.showHideFilterColumns,
    },
    {
      type: SettingsAccordionSubItemType.Button,
      label: t('Show/Hide Data on Marker'),
      disabledTooltipSelector: state => disabledForMapSnapshotsSelector(state, t('mapSettingsInfo.showHideDataDisabled')),
      infoTooltip: t('mapSettingsInfo.showHideDataOnMarker'),
      textSelector: () => t('Open'),
      ...buttons.showHideDataOnMarker,
    },
    {
      type: SettingsAccordionSubItemType.Button,
      label: t('Show/Hide Data from Export'),
      disabledTooltipSelector: state => disabledForMapSnapshotsSelector(state, t('mapSettingsInfo.showHideDataDisabled')),
      infoTooltip: t('mapSettingsInfo.showHideDataFromExport'),
      textSelector: () => t('Open'),
      ...buttons.showHideDataFromExport,
    },
    buttons.presentationalDataExportAuthorize?.onClick ? {
      type: SettingsAccordionSubItemType.Button,
      label: t('mapSettingsInfo.ExportingDataFeatures'),
      disabledTooltipSelector: state => presentationalMapsExportDisabledTooltipSelector(state, t),
      infoTooltip: t('mapSettingsInfo.ExportingDataFeatures'),
      textSelector: () => t('Authorize'),
      ...buttons.presentationalDataExportAuthorize,
    } : null,
    buttons.presentationalDataExportAllowTools?.onClick ? {
      type: SettingsAccordionSubItemType.Button,
      label: t('mapSettingsInfo.ExportingDataFeatures'),
      disabledTooltipSelector: state => presentationalMapsExportDisabledTooltipSelector(state, t),
      infoTooltip: t('mapSettingsInfo.ExportingDataFeatures'),
      textSelector: () => t('Activate'),
      ...buttons.presentationalDataExportAllowTools,
    } : null,
    {
      type: SettingsAccordionSubItemType.Button,
      label: t('Embed Map'),
      disabledTooltipSelector: always(EMPTY_MESSAGE),
      infoTooltip: t('Embed Map on Website, Blog, etc.'),
      textSelector: () => t('Open'),
      ...buttons.embedMap,
    },
  ]), [buttons.embedMap, buttons.presentationalDataExportAllowTools, buttons.presentationalDataExportAuthorize,
    buttons.restrictZoomLevels, buttons.showHideDataFromExport, buttons.showHideDataOnMarker,
    buttons.showHideFilterColumns, dropdowns.defaultOpenMapKey, dropdowns.defaultOpenMapToolPresentationalMap,
    dropdowns.searchBarFilterDefaultOption, t, toggles.allowSatelliteView, toggles.allowStreetView,
    toggles.boundaryTool, toggles.displayLegendOnly, toggles.displaySearchBox, toggles.displayTitleAndDescription,
    toggles.distanceCalculator, toggles.filterMenusOpen, toggles.filterTool, toggles.groupingTool,
    toggles.heatMappingTool, toggles.lassoTool, toggles.legendReplacesGrouping, toggles.locationFinder,
    toggles.locationFinderStartsOpen, toggles.mapKey, toggles.mapKeyOpen, toggles.mapToolsMenuOpen, toggles.mapZoom,
    toggles.markersVisibilityButton, toggles.radiusProximity, toggles.restrictMapPanning, toggles.routingDirections,
    toggles.searchBarAlwaysVisible, toggles.showMyLocation, toggles.showMyLocationDefault, toggles.showSearchFilter,
    toggles.themeStyle]);

  return useMemo(() => allItems.filter(notNullsy), [allItems]);
};
